import { db, byId, CURRENT_USER_ID, insert, whenPersisted, flowEditableName, loadTable } from './core';
import type { EntityRef, Recent } from '../store/ui';
import { flowTemplates, templateGraph } from '../../../../packages/canvas-runtime/flow-templates.mjs';

export interface FlowFilters {
  workspaceId?: string | null; projectId?: string; q?: string; view?: string;
  sort?: string; favorites?: EntityRef[] | null; recents?: Recent[] | null;
}

export function listFlows({ workspaceId, projectId, q, view = 'all', sort = 'updated', favorites = [], recents = [] }: FlowFilters = {}) {
  let rows = db.flows.filter(flow => {
    const project = byId('projects', flow.project_id);
    return project && project.status !== 'archived' && (!workspaceId || project.workspace_id === workspaceId)
      && (!projectId || flow.project_id === projectId);
  });
  if (q?.trim()) {
    const needle = q.trim().toLocaleLowerCase();
    rows = rows.filter(flow => [flow.name, flow.description, byId('projects', flow.project_id)?.name]
      .some(value => String(value || '').toLocaleLowerCase().includes(needle)));
  }
  if (view === 'mine') rows = rows.filter(flow => flow.created_by === CURRENT_USER_ID);
  if (view === 'favorites') rows = rows.filter(flow => favorites?.some(item => item.entity_type === 'flow' && item.entity_id === flow.id));
  const recentIds = (recents || []).filter(item => item.entity_type === 'flow').map(item => item.entity_id);
  if (view === 'recent') rows = rows.filter(flow => recentIds.includes(flow.id!));
  return [...rows].sort((a, b) => {
    if (view === 'recent') return recentIds.indexOf(a.id!) - recentIds.indexOf(b.id!);
    if (sort === 'name') return String(a.name).localeCompare(String(b.name));
    const field = sort === 'created' ? 'created_at' : 'updated_at';
    return String(b[field] || '').localeCompare(String(a[field] || ''));
  });
}

const emptyGraph = (): Record<string, unknown> => ({ schemaVersion: 2, app: 'bnb-nodes', catalogVersion: 1,
  viewport: { x: 40, y: 40, zoom: 1 }, nodes: [], edges: [], groups: [], annotations: { frames: [] } });

export async function createBlankFlow({ projectId, name, description = '', templateId = '', graph: initialGraph }: { projectId: string; name: string; description?: string; templateId?: string; graph?: Record<string, any> }) {
  const template = templateId ? flowTemplates.find(item => item.id === templateId) : null;
  if (templateId && !template) throw new Error('Unknown flow template.');
  const graph = template ? templateGraph(template.id) : initialGraph ? structuredClone(initialGraph) : emptyGraph();
  await Promise.all([loadTable('projects'), loadTable('brands')]);
  name = flowEditableName({ name, project_id: projectId });
  if (!name) throw new Error('Enter a flow name.');
  const project = byId('projects', projectId);
  if (!project || project.status === 'archived') throw new Error('Choose an available project.');
  return whenPersisted(insert('flows', { id: crypto.randomUUID(), project_id: projectId, name,
    description: description || template?.description || '', status: 'draft', graph, created_by: CURRENT_USER_ID }));
}

export async function createNodeStarterGraph(type: string) {
  const { paletteItems, paletteNode } = await import('../features/canvas/runtime/registry-bridge');
  const definition = paletteItems().find(item => item.type === type);
  if (!definition) throw new Error('Choose an available node.');
  const graph: any = emptyGraph();
  graph.nodes.push(paletteNode(type, 220, 160));
  return { graph, name: definition.title || type };
}

export async function importFlowGraphText(text: string) {
  const { importGraphText } = await import('../features/canvas/runtime/registry-bridge');
  return importGraphText(text);
}

export async function renameFlow(id: string, name: string) {
  await Promise.all([loadTable('projects'), loadTable('brands')]);
  name = flowEditableName({ ...byId('flows', id), name });
  if (!name) throw new Error('Enter a flow name.');
  // Persist a separate row so a failed request never replaces the visible name.
  const response = await fetch(`/api/v1/flows/${encodeURIComponent(id)}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }),
  });
  const saved = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(saved.error || 'Could not rename flow.');
  const flow = byId('flows', id);
  if (flow) Object.assign(flow, { name: saved.name, updated_at: saved.updated_at });
  window.dispatchEvent(new CustomEvent('cf:data-updated', { detail: 'flows' }));
}

export async function duplicateFlow(id: string) {
  const response = await fetch(`/api/v1/flows/${encodeURIComponent(id)}`);
  const original = await response.json();
  if (!response.ok || !original?.graph) throw new Error(original?.error || 'Could not load this flow.');
  const graph = structuredClone(original.graph);
  // Copies start without old execution state or generated previews.
  for (const node of graph.nodes || []) {
    for (const key of ['outputs', 'result', 'status', 'progress', 'requestId', 'error', 'done', 'dirty', 'statusDetail', 'progressDetail']) delete node[key];
  }
  return whenPersisted(insert('flows', { id: crypto.randomUUID(), project_id: original.project_id,
    name: `${original.name} (copy)`, description: original.description, graph, status: 'draft' }));
}
